import panelSelectorsGenerator from '../selectorsGenerator'
import assignmentsSelectorsGenerator from '../../usersAssignedToHierarchy/selectorsGenerator'
import commentsSelectorGenerator from '../../comments/selectorsGenerator'
import {createSelector} from 'reselect'
import {getCustomers, getProjects, getTasks} from '../../../entities/selectors'
import {getEditableCustomerIds, getEditableProjectIds, getEditableTaskIds, getManageableTaskIds} from '../../../taskManagement/common/selectors'
import {selectTaskCustomFields} from '../../../customFields/selectors'
import {canGenerateMoneyReports} from '../../../loggedInUser/selectors'
import {TaskPredefinedFieldNames} from '../../../next/constants'

export const getTaskPanelState = state => state.panels.task.edit

export const getOpenTaskId = state => getTaskPanelState(state).openTaskId

const getUpdatingState = state => getTaskPanelState(state).updatingState

export const isEditTaskPanelEditable = state => getTaskPanelState(state).canEdit

export const isNameUpdating = (state) => getUpdatingState(state).name
export const isDeadlineUpdating = (state) => getUpdatingState(state).deadline
export const isTypeOfWorkUpdating = (state) => getUpdatingState(state).typeOfWork
export const isEstimateUpdating = (state) => getUpdatingState(state).estimate
export const isDescriptionUpdating = (state) => getUpdatingState(state).description
export const isStatusUpdating = (state) => getUpdatingState(state).status
export const isMoveToDialogLoading = (state) => getUpdatingState(state).moveToDialog

const taskPanelSelectors = panelSelectorsGenerator(getTaskPanelState)
const assignmentsSelectors = assignmentsSelectorsGenerator(getTaskPanelState)

export const isLoaded = taskPanelSelectors.isLoaded
export const isLoading = taskPanelSelectors.isLoading
export const isPanelOpened = taskPanelSelectors.isPanelOpened
export const isNeedToLoadData = taskPanelSelectors.isNeedToLoadData
export const isTaskCompleted = taskPanelSelectors.isTaskCompleted
export const getCreationTimestamp = taskPanelSelectors.getCreationTimestamp
export const getTaskStatusId = taskPanelSelectors.getTaskStatusId
export const getTaskDeadline = taskPanelSelectors.getTaskDeadline
export const getTaskName = taskPanelSelectors.getTaskName
export const getEstimatedTime = taskPanelSelectors.getEstimatedTime
export const getSpentTime = taskPanelSelectors.getSpentTime
export const getTaskDescription = taskPanelSelectors.getTaskDescription
export const getBillableTypeName = taskPanelSelectors.getBillableTypeName
export const getBillingType = taskPanelSelectors.getBillingType
export const isWorkTypeBillable = taskPanelSelectors.isWorkTypeBillable
export const getTaskPanelErrors = taskPanelSelectors.getTaskPanelErrors

export const getTaskPanelTabKey = taskPanelSelectors.getTaskPanelTabKey
export const getLastTaskPanelTabKey = taskPanelSelectors.getLastTaskPanelTabKey

export const getAssignedUsers = assignmentsSelectors.getAssignedUsers
export const getAssignedManagers = assignmentsSelectors.getAssignedManagers
export const canModifyAnyUser = assignmentsSelectors.canModifyAnyUser
export const canModifyAnyManager = assignmentsSelectors.canModifyAnyManager

export const getTaskDetails = state => getTaskPanelState(state).taskDetails
export const isTaskDeletedSuccessfully = state => getTaskPanelState(state).taskDeletedSuccessfully
export const isTaskDeletedUnsuccessfully = state => getTaskPanelState(state).taskDeletedUnsuccessfully
export const getTaskCustomFieldsValues = state => getTaskPanelState(state).customFieldsValues

export const getTaskExecutorsSummary = state => getTaskPanelState(state).executorsSummary

export const getReportedTimeComments = state => getTaskPanelState(state).reportedTimeComments
export const isAllTimeCommentsLoaded = state => getTaskPanelState(state).allTimeCommentsLoaded
export const isTimeAndCommentsLoading = state => getTaskPanelState(state).timeAndCommentsLoading
export const getSortingSettings = state => getTaskPanelState(state).sortingSettings

export const getTaskAuditInfo = state => getTaskPanelState(state).auditInfo
export const isAllAuditHistoryLoaded = (state) => getTaskAuditInfo(state).allAuditHistoryLoaded
export const isAuditHistoryLoading = (state) => getTaskAuditInfo(state).auditHistoryLoading
export const getAuditActions = (state) => getTaskAuditInfo(state).auditActions
export const getLastAuditActions = (state) => getTaskAuditInfo(state).lastAuditActions

const getTaskProjectId = createSelector(
    getOpenTaskId,
    getTasks,
    (id, tasks) => {
        return (tasks[id]) ? tasks[id].projectId : -1
    }
)

const getProjectFromTask = createSelector(
    getTaskProjectId,
    getProjects,
    (id, projects) => {
        return (projects[id]) ? projects[id] : null
    }
)

const getTaskCustomerId = createSelector(
    getOpenTaskId,
    getTasks,
    (id, tasks) => {
        return (tasks[id]) ? tasks[id].customerId : -1
    }
)

export const getOpenTaskNextId = createSelector(
    getOpenTaskId,
    getTasks,
    (id, tasks) => {
        return tasks[id] ? tasks[id].nextId : -1
    }
)

export const getIsOpenTaskCompleted = createSelector(
    getOpenTaskId,
    getTasks,
    (id, tasks) => {
        return tasks[id] ? tasks[id].completed : -1
    }
)

const getCustomerFromTask = createSelector(
    getTaskCustomerId,
    getCustomers,
    (id, customers) => {
        return (customers[id]) ? customers[id] : null
    }
)

export const getProjectName = createSelector(
    getProjectFromTask,
    (project) => {
        return project ? project.name : ''
    }
)

export const getCustomerName = createSelector(
    getCustomerFromTask,
    (customer) => {
        return customer ? customer.name : ''
    }
)

export const isTaskManageable = createSelector(
    getOpenTaskId,
    getManageableTaskIds,
    (taskId, manageableTaskIds) => {
        return !!manageableTaskIds[taskId]
    }
)

export const canReopenProject = createSelector(
    getTaskProjectId,
    getProjectFromTask,
    getTaskCustomerId,
    getCustomerFromTask,
    getEditableProjectIds,
    getEditableCustomerIds,
    (projectId, project, customerId, customer, editableProjectIds, editableCustomerIds) => {
        return (!!project && !project.archived) || (!!editableProjectIds[projectId] && !!customer && !customer.archived) || !!editableCustomerIds[customerId]
    }
)

export const canMoveTask = createSelector(
    getOpenTaskId,
    getEditableTaskIds,
    (taskId, editableTaskIds) => {
        return !!editableTaskIds[taskId]
    }
)

const commentsSelector = commentsSelectorGenerator(getTaskPanelState)

export const getComments = commentsSelector.getComments
export const getUsers = commentsSelector.getUsers
export const getCommentsCount = commentsSelector.getCommentsCount
export const getAllCommentsUserIds = commentsSelector.getAllCommentsUserIds
export const getRecentlyAddedCommentsIds = commentsSelector.getRecentlyAddedCommentsIds
export const needMoreComments = commentsSelector.needMoreComments
export const isNewCommentsOnTop = commentsSelector.isNewCommentsOnTop
export const getNewestCommentId = commentsSelector.getNewestCommentId
export const getCommentsScrollTo = commentsSelector.getCommentsScrollTo
export const getEditingCommentId = commentsSelector.getEditingCommentId
export const getEditingCommentText = commentsSelector.getEditingCommentText
export const isCommentsLoading = commentsSelector.isLoading
export const getUpdatingCommentId = commentsSelector.getUpdatingCommentId
export const getNewCommentsTexts = commentsSelector.getNewCommentsTexts
export const isSendingComment = commentsSelector.isSendingComment

export const getTaskNewComment = createSelector(
    getOpenTaskId,
    commentsSelector.getNewCommentsTexts,
    (taskId, comments) => comments[taskId] ? comments[taskId] : ''
)

export const isShowNewCommentInput = createSelector(
    commentsSelector.isShowNewCommentInput,
    getOpenTaskId,
    commentsSelector.getNewCommentsTexts,
    (showInput, taskId, comments) => showInput || comments[taskId] !== undefined
)

export const isPanelOpenedFromUrl = createSelector(
    getTaskPanelState,
    panelState => panelState.openedFromUrl
)

export const getReadOnlyFields = createSelector(
    selectTaskCustomFields,
    isEditTaskPanelEditable,
    isTaskManageable,
    canGenerateMoneyReports,
    (customFields, editable, isTaskManageable, canGenerateMoneyReport) => {
        if (editable) return []

        const readonly = customFields.map(cf => cf.fieldName)
        readonly.push(TaskPredefinedFieldNames.Deadline)
        if (!(canGenerateMoneyReport && isTaskManageable)) {
            readonly.push(TaskPredefinedFieldNames.TypeOfWork)
        }
        return readonly
    }
)



// WEBPACK FOOTER //
// ./web/js/panels/task/editPanel/selectors.js